import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CircularProgress, Tooltip, IconButton } from "@mui/material";
import { Play, Copy } from "lucide-react";
import toast from "react-hot-toast";
import CustomModal from "../common/CustomModal";
import StatusChip from "../common/StatusChip";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import AdsAPIs from "../../apiRoutes/Ads";
import {
  GET_AD_MODERATIONS_QUERY_KEY,
  GET_MODERATION_CONTEXT_QUERY_KEY,
} from "../../utils/constants/queryKeys";
import {
  formatDisplayDateTime,
  formatSeconds,
} from "../../utils/helperFunctions";
import { isOrphanId } from "../../utils/constants/ads";
import { getPlayableAssetUrl } from "../../utils/assetUrl";
import {
  getYouTubeVideoId,
  buildYouTubeEmbedSrc,
  YOUTUBE_IFRAME_ALLOW,
} from "../../utils/youtube";
import { copyTextToClipboard } from "../../utils/clipboard";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import SegmentedToggle from "./SegmentedToggle";
import DecisionConfirmModal from "./DecisionConfirmModal";
import { STATUS_CHANGE_ACTIONS } from "./constants";
import { IssueChips, ModerationIssuePicker } from "./ModerationIssues";

/**
 * Ad moderation context modal: sticky video column + scrolling detail column
 * (identity, decision zone, issues/violations, comments, activity timeline, notes).
 * Owns its own context query, mutations and edit state — mount it only while open
 * (state resets on unmount) and pass the permission flags from the section.
 */
// Tags exactly as stored: taxonomy codes, or old free-text tags on rows tagged before IPD-395.
const issuesOf = (m) =>
  Array.isArray(m?.issues_highlighted)
    ? m.issues_highlighted.map((i) => String(i || "").trim()).filter(Boolean)
    : [];

export default function ModerationContextModal({
  adId,
  adModerateId,
  onClose,
  canReviewModeration,
  canEnterErrors,
}) {
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();

  const { data: contextResult, isLoading: contextLoading } = useQuery({
    queryKey: [...GET_MODERATION_CONTEXT_QUERY_KEY, adId, adModerateId],
    queryFn: () =>
      AdsAPIs.getModerationContext({
        axiosPrivate,
        ad_id: adId,
        ad_moderate_id: adModerateId,
      }),
    enabled: !!adId && Number(adId) !== -7,
  });
  const contextData = contextResult?.data ?? {};
  const contextAd = contextData.ad ?? null;
  const contextDeliveries = contextData.ad_deliveries ?? [];
  const contextHistory = contextData.moderation_history ?? [];
  const activeModeration = contextData.active_moderation ?? null;
  const videoPreviewUrl = (contextData.video_preview_url || "").trim();
  const iterationCount = contextData.iteration_count ?? 0;

  const [contextActionFlow, setContextActionFlow] = useState(null);
  const [actionNotes, setActionNotes] = useState("");
  const [contextComment, setContextComment] = useState("");
  const videoRef = useRef(null);
  const loadedModerationIdRef = useRef(null);
  const [contextAmNotes, setContextAmNotes] = useState("");
  const [contextAmErrors, setContextAmErrors] = useState("");
  const [contextAmIssues, setContextAmIssues] = useState([]);

  useEffect(() => {
    if (!activeModeration) return;
    if (loadedModerationIdRef.current === activeModeration.id) return;
    loadedModerationIdRef.current = activeModeration.id;
    setContextAmNotes(
      Array.isArray(activeModeration.notes)
        ? activeModeration.notes.join("\n")
        : activeModeration.notes || ""
    );
    setContextAmErrors(
      Array.isArray(activeModeration.errors)
        ? activeModeration.errors.join("\n")
        : ""
    );
    setContextAmIssues(issuesOf(activeModeration));
  }, [activeModeration]);

  const invalidateContext = () => {
    if (adId)
      queryClient.invalidateQueries({
        queryKey: [...GET_MODERATION_CONTEXT_QUERY_KEY, adId, adModerateId],
      });
  };

  const commentMutation = useMutation({
    mutationFn: (notes) =>
      AdsAPIs.addModerationContextComment({
        axiosPrivate,
        ad_id: adId,
        ad_moderate_id: activeModeration?.id,
        notes,
      }),
    onSuccess: () => {
      invalidateContext();
      setContextComment("");
      toast.success("Comment added.");
    },
  });

  const saveAmMutation = useMutation({
    mutationFn: (payload) =>
      AdsAPIs.updateModeration({
        axiosPrivate,
        id: activeModeration?.id,
        payload,
      }),
    onSuccess: () => {
      invalidateContext();
      toast.success("Saved.");
    },
  });

  const approveRejectMutation = useMutation({
    mutationFn: (payload) =>
      AdsAPIs.updateModeration({
        axiosPrivate,
        id: activeModeration?.id,
        payload,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: GET_AD_MODERATIONS_QUERY_KEY });
      invalidateContext();
      toast.success(
        `Ad ${variables.status === "approved" ? "approved" : "rejected"}.`
      );
      setContextActionFlow(null);
      setActionNotes("");
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: GET_AD_MODERATIONS_QUERY_KEY });
      invalidateContext();
    },
  });

  const submitApproveReject = () => {
    if (contextActionFlow === "rejected" && contextAmIssues.length === 0) {
      toast.error("Select at least one issue to reject.");
      return;
    }
    const payload = {
      status: contextActionFlow,
      notes: actionNotes ? [actionNotes] : undefined,
      issues_highlighted:
        contextActionFlow === "rejected" ? contextAmIssues : undefined,
      errors:
        contextActionFlow === "rejected" && canEnterErrors
          ? contextAmErrors
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean)
          : undefined,
    };
    approveRejectMutation.mutate(payload);
  };

  const initialNotesStr = activeModeration
    ? Array.isArray(activeModeration.notes)
      ? activeModeration.notes.join("\n")
      : activeModeration.notes || ""
    : "";
  const initialIssues = issuesOf(activeModeration);
  const notesDirty = contextAmNotes.trim() !== initialNotesStr.trim();
  const issuesDirty =
    JSON.stringify([...contextAmIssues].sort()) !==
    JSON.stringify([...initialIssues].sort());
  const initialErrorsStr =
    activeModeration && Array.isArray(activeModeration.errors)
      ? activeModeration.errors.join("\n")
      : "";
  const errorsDirty = contextAmErrors.trim() !== initialErrorsStr.trim();
  const isSavingIssues =
    saveAmMutation.isPending &&
    saveAmMutation.variables?.issues_highlighted !== undefined;
  const isSavingErrors =
    saveAmMutation.isPending && saveAmMutation.variables?.errors !== undefined;

  const handleSaveIssues = () => {
    saveAmMutation.mutate({ issues_highlighted: contextAmIssues });
  };

  const handleSaveErrors = () => {
    saveAmMutation.mutate({
      errors: contextAmErrors
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
    });
  };

  // With Option A (single AdModerate row per hook), all history for a hook shares one ad_moderate_id.
  // No need for relatedModerateIds chain — just match directly on activeModeration.id.
  const currentIteration = activeModeration?.iteration ?? null;

  // Comments across all iterations of the same hook (same ad_moderate_id)
  const contextComments = contextHistory.filter(
    (h) =>
      (h.action || "").toLowerCase() === "comment" &&
      activeModeration?.id != null &&
      Number(h.ad_moderate_id) === Number(activeModeration.id)
  );
  // Latest approve/reject scoped to this moderation record AND current iteration, sorted by created_on
  const lastModerationAction =
    [...contextHistory]
      .filter((h) => {
        const a = (h.action || "").toLowerCase();
        if (!STATUS_CHANGE_ACTIONS.has(a)) return false;
        if (activeModeration?.id == null) return false;
        if (Number(h.ad_moderate_id) !== Number(activeModeration.id))
          return false;
        // Scope to current iteration so old-iteration status pill doesn't bleed into new iteration
        if (
          currentIteration != null &&
          Number(h.iteration) !== Number(currentIteration)
        )
          return false;
        return true;
      })
      .sort((a, b) => new Date(a.created_on || 0) - new Date(b.created_on || 0))
      .at(-1) ?? null;
  const lastActionBy = lastModerationAction?.performed_by_name || "";
  const lastActionAt = lastModerationAction?.created_on || null;
  // Use activeModeration directly — activeDeliveryForIteration was unreliable when multiple hooks
  // share the same iteration number (finds the wrong delivery)
  const deliveryFinalizedStatus = (
    activeModeration?.finalized_status ?? ""
  ).trim();
  const isModerationFinalized = !!(
    activeModeration?.finalized_at ||
    (activeModeration?.finalized_status || "").trim()
  );
  const moderationStatus = (activeModeration?.status || "")
    .trim()
    .toLowerCase();
  const hasModerationDecision =
    moderationStatus === "approved" || moderationStatus === "rejected";
  const showAcceptRejectButtons =
    activeModeration && !isModerationFinalized && !hasModerationDecision;

  const isDeliveryTimelineAction = (action) => {
    const a = (action || "").toLowerCase();
    return a === "delivered" || a === "resubmitted";
  };
  const deliveryEventsFromLogs = (contextHistory || [])
    .filter((h) => {
      if (!isDeliveryTimelineAction(h.action)) return false;
      if (activeModeration?.id == null) return false;
      return Number(h.ad_moderate_id) === Number(activeModeration.id);
    })
    .map((h) => ({
      type: "delivery",
      id: `h-${h.id}`,
      deliveryKind: (h.action || "").toLowerCase(),
      created_on: h.created_on,
      delivery_id: h.snapshot_delivery_id,
      iteration:
        h.snapshot_iteration != null ? h.snapshot_iteration : h.iteration,
      hook_number:
        h.snapshot_hook_number != null ? h.snapshot_hook_number : undefined,
      hook_name: h.snapshot_hook_name || undefined,
      ad_asset_url: (h.snapshot_ad_asset_url || "").trim(),
      performed_by_name: h.performed_by_name,
    }));
  const deliveryIdsWithInitialLog = new Set(
    deliveryEventsFromLogs
      .filter((e) => e.deliveryKind === "delivered")
      .map((e) => e.delivery_id)
      .filter((id) => id != null)
  );
  // Legacy fallback: show deliveries for the same hook that don't have a log entry
  const sameHookDeliveryIds = new Set(
    deliveryEventsFromLogs.map((e) => e.delivery_id).filter((id) => id != null)
  );
  const currentHookName = (activeModeration?.hook_name || "").trim();
  const deliveryEventsFromRows = (contextDeliveries || [])
    .filter((d) => {
      if (deliveryIdsWithInitialLog.has(d.id) || sameHookDeliveryIds.has(d.id))
        return false;
      if (!currentHookName) return false;
      return (d.hook_name || "").trim() === currentHookName;
    })
    .map((d) => ({
      type: "delivery",
      id: `d-${d.id}`,
      deliveryKind: "legacy_row",
      created_on: d.created_on,
      delivery_id: d.id,
      iteration: d.iteration,
      hook_number: d.hook_number,
      hook_name: d.hook_name,
      ad_asset_url: (d.ad_asset_url || "").trim(),
      performed_by_name: undefined,
    }));
  const moderationTimelineItems = (contextHistory || [])
    .filter((h) => {
      if (isDeliveryTimelineAction(h.action)) return false;
      if (activeModeration?.id == null) return false;
      return Number(h.ad_moderate_id) === Number(activeModeration.id);
    })
    .map((h) => ({ type: "moderation", ...h, id: `h-${h.id}` }));
  const timelineItems = [
    ...deliveryEventsFromLogs,
    ...deliveryEventsFromRows,
    ...moderationTimelineItems,
  ].sort((a, b) => new Date(b.created_on || 0) - new Date(a.created_on || 0));

  return (
    <CustomModal
      open
      onClose={onClose}
      title={
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 normal-case">
          <span className="text-[17px] font-semibold text-gray-900">
            Ad moderation
          </span>
          {activeModeration && (
            <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] font-normal text-gray-500">
              {contextAd?.id != null && (
                <span className="inline-flex items-center gap-1 rounded-md bg-gray-50 border border-gray-200 px-1.5 py-0.5">
                  <span className="uppercase tracking-wide">Parent Ad ID</span>
                  <span className="font-mono font-semibold text-gray-800">
                    #{contextAd.id}
                  </span>
                  <Tooltip title="Copy">
                    <IconButton
                      size="small"
                      onClick={() =>
                        copyTextToClipboard(
                          String(contextAd.id),
                          "Parent Ad ID copied"
                        )
                      }
                      aria-label="Copy Parent Ad ID"
                      sx={{ p: 0.125 }}
                    >
                      <Copy className="w-3 h-3" />
                    </IconButton>
                  </Tooltip>
                </span>
              )}
              {!isOrphanId(activeModeration.ad_deliver_id) &&
                activeModeration.ad_deliver_id != null && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-gray-50 border border-gray-200 px-1.5 py-0.5">
                    <span className="uppercase tracking-wide">Delivery ID</span>
                    <span className="font-mono font-semibold text-gray-800">
                      #{activeModeration.ad_deliver_id}
                    </span>
                    <Tooltip title="Copy">
                      <IconButton
                        size="small"
                        onClick={() =>
                          copyTextToClipboard(
                            String(activeModeration.ad_deliver_id),
                            "Delivery ID copied"
                          )
                        }
                        aria-label="Copy Delivery ID"
                        sx={{ p: 0.125 }}
                      >
                        <Copy className="w-3 h-3" />
                      </IconButton>
                    </Tooltip>
                  </span>
                )}
              <span className="inline-flex items-center gap-1 rounded-md bg-gray-50 border border-gray-200 px-1.5 py-0.5">
                <span className="uppercase tracking-wide">Moderation ID</span>
                <span className="font-mono font-semibold text-gray-800">
                  #{activeModeration.id}
                </span>
                <Tooltip title="Copy">
                  <IconButton
                    size="small"
                    onClick={() =>
                      copyTextToClipboard(
                        String(activeModeration.id),
                        "Moderation ID copied"
                      )
                    }
                    aria-label="Copy Moderation ID"
                    sx={{ p: 0.125 }}
                  >
                    <Copy className="w-3 h-3" />
                  </IconButton>
                </Tooltip>
              </span>
            </span>
          )}
        </span>
      }
      isButtonsRequired={false}
      className="md:w-[85%] lg:w-[80%] max-h-[90vh]"
    >
      {/* ── Two-column layout: sticky video left · scrolling content right ── */}
      <div className="-mx-4 -my-2 flex min-h-0">
        {/* ── LEFT: Video column (sticky) ──────────────────────────── */}
        <div className="w-[42%] shrink-0 border-r border-gray-100 flex flex-col items-center gap-2 px-4 py-4 sticky top-0 self-start">
          {contextLoading ? (
            <div className="flex flex-col items-center justify-center h-48 gap-2">
              <CircularProgress size={22} sx={{ color: "#7f7ad5" }} />
              <span className="text-xs font-medium text-[#7f7ad5]">
                Loading…
              </span>
            </div>
          ) : videoPreviewUrl ? (
            <>
              {getYouTubeVideoId(videoPreviewUrl) ? (
                <div className="h-[78vh] max-h-[78vh] max-w-full aspect-[9/16] mx-auto rounded-lg overflow-hidden border border-gray-200 bg-black">
                  <iframe
                    title="Video preview"
                    src={buildYouTubeEmbedSrc(
                      getYouTubeVideoId(videoPreviewUrl)
                    )}
                    className="w-full h-full border-0"
                    allow={YOUTUBE_IFRAME_ALLOW}
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="h-[78vh] max-h-[78vh] max-w-full aspect-[9/16] mx-auto rounded-lg overflow-hidden border border-gray-200 bg-black">
                  <video
                    ref={videoRef}
                    src={videoPreviewUrl}
                    controls
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
              <a
                href={videoPreviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium text-[#7f7ad5] hover:underline"
              >
                Open in new tab
              </a>
              {contextData.video_preview_url ? (
                <span className="text-[10px] font-medium inline-flex items-center gap-1">
                  {(contextData.video_preview_url || "").includes(
                    "seekhoapp.com"
                  ) ||
                  (contextData.video_preview_url || "").includes(
                    "amazonaws.com"
                  ) ? (
                    <>
                      <span
                        style={{
                          display: "inline-block",
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "#10b981",
                        }}
                      />
                      <span className="text-emerald-600">S3 / CDN</span>
                    </>
                  ) : (
                    <>
                      <span
                        style={{
                          display: "inline-block",
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "#f59e0b",
                        }}
                      />
                      <span className="text-amber-600">
                        Drive (pending migration)
                      </span>
                    </>
                  )}
                </span>
              ) : null}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center gap-1 h-[78vh] max-h-[78vh] max-w-full aspect-[9/16] mx-auto rounded-lg border border-dashed border-gray-200">
              <Play className="w-10 h-10 text-gray-300" />
              <span className="text-[10px] text-gray-400">No video</span>
            </div>
          )}
        </div>

        {/* ── RIGHT: Scrollable content column ─────────────────────── */}
        <div className="flex-1 min-w-0 px-5 py-4 space-y-4 overflow-y-auto max-h-[85vh]">
          {contextLoading ? (
            <div className="flex items-center justify-center py-12 gap-2">
              <CircularProgress size={24} sx={{ color: "#7f7ad5" }} />
              <span className="text-sm font-medium text-[#7f7ad5]">
                Loading…
              </span>
            </div>
          ) : (
            <>
              {/* 1 ── AD IDENTITY ─────────────────────────────────────── */}
              {contextAd && (
                <div className="space-y-2">
                  {contextAd.topic && (
                    <p className="text-base font-semibold text-gray-900 leading-snug break-words">
                      {contextAd.topic}
                    </p>
                  )}
                  {activeModeration?.ad_name && (
                    <div>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
                        Ad name
                      </p>
                      <p className="text-xs font-mono text-gray-700 break-all">
                        {activeModeration.ad_name}
                      </p>
                    </div>
                  )}
                  {(contextAd.for_app_name ||
                    contextAd.module ||
                    contextAd.ad_type) && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      {contextAd.for_app_name && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-white text-gray-600 border-gray-200">
                          {contextAd.for_app_name}
                        </span>
                      )}
                      {contextAd.module && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-white text-gray-600 border-gray-200">
                          {contextAd.module}
                        </span>
                      )}
                      {contextAd.ad_type && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-white text-gray-500 border-gray-200 capitalize">
                          {contextAd.ad_type}
                        </span>
                      )}
                    </div>
                  )}
                  {(contextAd.category ||
                    contextAd.track ||
                    contextAd.track_number != null) && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                      {contextAd.category && (
                        <span className="inline-flex items-center gap-1">
                          <span className="font-semibold text-gray-400 uppercase tracking-wide text-[10px]">
                            Category
                          </span>
                          <span className="text-gray-600">
                            {contextAd.category}
                          </span>
                        </span>
                      )}
                      {contextAd.track && (
                        <span className="inline-flex items-center gap-1">
                          <span className="font-semibold text-gray-400 uppercase tracking-wide text-[10px]">
                            Track
                          </span>
                          <span className="text-gray-600">
                            {contextAd.track}
                          </span>
                        </span>
                      )}
                      {contextAd.track_number != null && (
                        <span className="inline-flex items-center gap-1">
                          <span className="font-semibold text-gray-400 uppercase tracking-wide text-[10px]">
                            Track #
                          </span>
                          <span className="text-gray-600">
                            {contextAd.track_number}
                          </span>
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* 2 ── REVIEW: ISSUES + ERRORS ─────────────────────────── */}
              {activeModeration && (
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                        Issues
                      </p>
                      <ModerationIssuePicker
                        value={contextAmIssues}
                        onChange={setContextAmIssues}
                        disabled={
                          !canReviewModeration ||
                          isModerationFinalized ||
                          hasModerationDecision
                        }
                      />
                      {issuesDirty &&
                        canReviewModeration &&
                        !isModerationFinalized &&
                        !hasModerationDecision && (
                          <Button
                            size="sm"
                            onClick={handleSaveIssues}
                            disabled={saveAmMutation.isPending}
                          >
                            {isSavingIssues ? "Saving…" : "Save"}
                          </Button>
                        )}
                    </div>
                    {canEnterErrors && (
                      <div className="space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                          Errors
                        </p>
                        <textarea
                          value={contextAmErrors}
                          onChange={(e) => setContextAmErrors(e.target.value)}
                          placeholder="One per line, e.g. 0:30 - wrong logo"
                          rows={5}
                          readOnly={
                            !canReviewModeration ||
                            isModerationFinalized ||
                            hasModerationDecision
                          }
                          className="w-full rounded-md border border-input px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-ring read-only:bg-muted/40"
                        />
                        {errorsDirty &&
                          canReviewModeration &&
                          !isModerationFinalized &&
                          !hasModerationDecision && (
                            <Button
                              size="sm"
                              onClick={handleSaveErrors}
                              disabled={saveAmMutation.isPending}
                            >
                              {isSavingErrors ? "Saving…" : "Save"}
                            </Button>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 4 ── DECISION ZONE ───────────────────────────────────── */}
              {activeModeration &&
                (() => {
                  const fin = (deliveryFinalizedStatus || "")
                    .toLowerCase()
                    .trim();
                  const st = (activeModeration.status || "").toLowerCase();
                  const isFinalApproved = fin === "approved";
                  const isFinalRejected = fin === "rejected";
                  const isModerationApproved =
                    !isFinalApproved && !isFinalRejected && st === "approved";
                  const isModerationRejected =
                    !isFinalApproved && !isFinalRejected && st === "rejected";
                  const hasDecision =
                    isFinalApproved ||
                    isFinalRejected ||
                    isModerationApproved ||
                    isModerationRejected;

                  const zoneClass = isFinalApproved
                    ? "border-teal-300 bg-teal-50/40"
                    : isFinalRejected
                      ? "border-red-300 bg-red-50/40"
                      : isModerationApproved
                        ? "border-emerald-200 bg-emerald-50/30"
                        : isModerationRejected
                          ? "border-rose-200 bg-rose-50/30"
                          : "border-gray-200 bg-gray-50/50";

                  const chipStatus = isFinalApproved
                    ? "final_approved"
                    : isFinalRejected
                      ? "final_rejected"
                      : isModerationApproved
                        ? "moderation_approved"
                        : isModerationRejected
                          ? "moderation_rejected"
                          : "none";

                  const statusText = isFinalApproved
                    ? "Final approved"
                    : isFinalRejected
                      ? "Final rejected"
                      : isModerationApproved
                        ? "Last approved"
                        : isModerationRejected
                          ? "Last rejected"
                          : "No action yet";

                  return (
                    <div
                      className={cn(
                        "rounded-xl border-2 p-4 space-y-3",
                        zoneClass
                      )}
                    >
                      {/* header row: label + iteration chip */}
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                          Decision
                        </p>
                        {(activeModeration?.iteration != null ||
                          iterationCount > 0) && (
                          <span className="inline-flex flex-wrap items-center gap-1.5 px-2 py-0.5 rounded bg-white/80 border border-gray-200 text-xs text-gray-600">
                            <span className="font-medium break-all">
                              {activeModeration?.hook_name ||
                                `Ad ${activeModeration?.hook_number ?? 1}`}
                            </span>
                            <span className="text-gray-300">·</span>
                            <span className="shrink-0">
                              Iteration{" "}
                              <span className="font-semibold">
                                {activeModeration?.iteration ?? "—"}
                              </span>{" "}
                              of {iterationCount || "—"}
                            </span>
                          </span>
                        )}
                      </div>

                      {/* status pill */}
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <StatusChip
                          subtle
                          status={chipStatus}
                          label={statusText}
                        />
                        {hasDecision && (lastActionBy || lastActionAt) && (
                          <span className="inline-flex flex-wrap items-center gap-x-1 text-xs text-gray-500">
                            {lastActionBy && (
                              <span className="font-mono">{lastActionBy}</span>
                            )}
                            {lastActionBy && lastActionAt && (
                              <span className="opacity-60">·</span>
                            )}
                            {lastActionAt && (
                              <span>{formatDisplayDateTime(lastActionAt)}</span>
                            )}
                          </span>
                        )}
                      </div>

                      {/* action controls */}
                      <div className="flex flex-wrap items-center gap-3">
                        {showAcceptRejectButtons && canReviewModeration && (
                          <SegmentedToggle
                            leftLabel="Approve"
                            rightLabel="Reject"
                            active={
                              moderationStatus === "approved"
                                ? "left"
                                : moderationStatus === "rejected"
                                  ? "right"
                                  : null
                            }
                            onLeft={() => setContextActionFlow("approved")}
                            onRight={() => setContextActionFlow("rejected")}
                            disabled={approveRejectMutation.isPending}
                            leftClass={{
                              active: "bg-emerald-600 text-white",
                              inactive:
                                "bg-white text-emerald-700 hover:bg-emerald-50",
                            }}
                            rightClass={{
                              active: "bg-rose-600 text-white",
                              inactive:
                                "bg-white text-rose-700 hover:bg-rose-50",
                            }}
                          />
                        )}
                      </div>
                    </div>
                  );
                })()}

              {/* 5 ── COMMENTS ────────────────────────────────────────── */}
              {activeModeration && (
                <div className="space-y-2 pt-4 border-t border-gray-100">
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    Comments
                  </p>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {contextComments.length ? (
                      contextComments.map((c) => (
                        <div
                          key={c.id}
                          className="bg-gray-50 rounded-md px-3 py-2 border border-gray-100"
                        >
                          <p className="text-sm text-gray-800">
                            {c.comments || "—"}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {c.performed_by_name || "—"} ·{" "}
                            {formatDisplayDateTime(c.created_on)}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-400 italic">
                        No comments yet.
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <textarea
                      value={contextComment}
                      onChange={(e) => setContextComment(e.target.value)}
                      onFocus={() => {
                        if (contextComment) return;
                        const v = videoRef.current;
                        if (!v || !Number.isFinite(v.currentTime)) return;
                        setContextComment(
                          `At ${formatSeconds(v.currentTime)} - `
                        );
                      }}
                      placeholder="Add a comment…"
                      rows={2}
                      className="flex-1 rounded border border-input px-2 py-1.5 text-sm read-only:bg-muted/40"
                      readOnly={!canReviewModeration}
                      disabled={commentMutation.isPending}
                    />
                    {canReviewModeration && (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() =>
                          commentMutation.mutate(contextComment.trim())
                        }
                        disabled={
                          !contextComment.trim() || commentMutation.isPending
                        }
                      >
                        Submit
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* 6 ── ACTIVITY TIMELINE ───────────────────────────────── */}
              <section className="pt-4 border-t border-gray-100">
                <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  Activity timeline
                </h4>
                {timelineItems.length > 0 ? (
                  <div className="relative pl-1">
                    <ul className="space-y-0">
                      {timelineItems.map((item, idx) => {
                        const act = (item.action || "").toLowerCase();
                        const isModApprove =
                          act === "approved" || act === "final approved";
                        const isModReject =
                          act === "rejected" || act === "final rejected";
                        return (
                          <li
                            key={item.id}
                            className="relative flex gap-3 pb-3 last:pb-0"
                          >
                            {idx < timelineItems.length - 1 && (
                              <>
                                <span
                                  aria-hidden
                                  className="absolute left-3 top-6 bottom-0 w-px bg-gray-200"
                                />
                                <span
                                  aria-hidden
                                  className="absolute top-[calc(50%+12px)] -translate-y-1/2 left-[8.5px] block w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-b-[7px] border-b-gray-300"
                                />
                              </>
                            )}
                            <span
                              className={`relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-xs ${item.type === "delivery" ? "border-blue-400 bg-blue-50 text-blue-700" : isModApprove ? "border-emerald-400 bg-emerald-50 text-emerald-700" : isModReject ? "border-rose-400 bg-rose-50 text-rose-700" : item.action === "updated" ? "border-primaryPurple bg-primaryPurple/10 text-primaryPurple" : "border-gray-400 bg-gray-100 text-gray-600"}`}
                            >
                              {item.type === "delivery"
                                ? "D"
                                : isModApprove
                                  ? "✓"
                                  : isModReject
                                    ? "✕"
                                    : item.action === "updated"
                                      ? "✎"
                                      : "•"}
                            </span>
                            <div className="min-w-0 flex-1 pt-0.5">
                              <p className="text-sm font-medium text-gray-900">
                                {item.type === "delivery"
                                  ? `${item.hook_name || `Ad ${item.hook_number ?? 1}`} ${item.deliveryKind === "resubmitted" ? "resubmitted" : "delivered"}${item.iteration != null ? ` (iteration ${item.iteration})` : ""}${item.performed_by_name ? ` · ${item.performed_by_name}` : ""}`
                                  : item.action === "comment"
                                    ? `Comment by ${item.performed_by_name || "—"}`
                                    : `${(item.action || "").charAt(0).toUpperCase() + (item.action || "").slice(1)} by ${item.performed_by_name || "System"}`}
                              </p>
                              {item.type === "delivery" &&
                                (item.s3_asset_url || item.ad_asset_url) &&
                                (() => {
                                  const href = getPlayableAssetUrl(item);
                                  return (
                                    <a
                                      href={href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={href}
                                      className="mt-0.5 block text-xs text-primary hover:underline truncate max-w-full"
                                    >
                                      {href.length > 52
                                        ? `${href.slice(0, 52)}…`
                                        : href}
                                    </a>
                                  );
                                })()}
                              {item.type === "moderation" &&
                                act === "rejected" &&
                                Array.isArray(item.errors) &&
                                item.errors.length > 0 && (
                                  <ul className="mt-1 text-xs text-gray-700 list-disc list-inside space-y-0.5 max-h-32 overflow-y-auto">
                                    {item.errors.map((e, idx) => (
                                      <li key={idx}>{e}</li>
                                    ))}
                                  </ul>
                                )}
                              {item.type === "moderation" &&
                                act === "rejected" &&
                                Array.isArray(item.violation_categories) &&
                                item.violation_categories.length > 0 && (
                                  <p className="mt-1 text-xs text-gray-600">
                                    <span className="font-medium text-gray-500">
                                      Categories:{" "}
                                    </span>
                                    {item.violation_categories.join(", ")}
                                  </p>
                                )}
                              {item.type === "moderation" &&
                                act === "rejected" &&
                                Array.isArray(item.issues_highlighted) &&
                                item.issues_highlighted.length > 0 && (
                                  <div className="mt-1">
                                    <IssueChips
                                      codes={item.issues_highlighted}
                                    />
                                  </div>
                                )}
                              {item.type === "moderation" && item.comments && (
                                <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">
                                  {item.comments}
                                </p>
                              )}
                              <p className="mt-0.5 text-xs text-gray-400">
                                {formatDisplayDateTime(item.created_on) ?? "—"}
                              </p>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 py-2">No activity yet.</p>
                )}
              </section>

              {/* 7 ── NOTES ───────────────────────────────────────────── */}
              {activeModeration && (
                <div className="space-y-1.5 pt-4 border-t border-gray-100">
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    Notes
                  </p>
                  <textarea
                    value={contextAmNotes}
                    onChange={(e) => setContextAmNotes(e.target.value)}
                    placeholder="Internal only, not visible to delivery team. Use it to share notes with other moderators..."
                    rows={3}
                    readOnly={!canReviewModeration}
                    disabled={saveAmMutation.isPending}
                    className="w-full rounded border border-input px-2 py-1.5 text-sm resize-y read-only:bg-muted/40"
                  />
                  {notesDirty && canReviewModeration && (
                    <Button
                      size="sm"
                      onClick={() =>
                        saveAmMutation.mutate({
                          notes: contextAmNotes
                            .split("\n")
                            .map((s) => s.trim())
                            .filter(Boolean),
                        })
                      }
                      disabled={saveAmMutation.isPending}
                    >
                      {saveAmMutation.isPending ? "Saving…" : "Save notes"}
                    </Button>
                  )}
                </div>
              )}

              {!contextAd &&
                !contextLoading &&
                contextDeliveries.length === 0 &&
                contextHistory.length === 0 && (
                  <p className="text-sm text-gray-500 py-4">
                    No context for this ad.
                  </p>
                )}
            </>
          )}
        </div>
      </div>

      {contextActionFlow && canReviewModeration && (
        <DecisionConfirmModal
          action={contextActionFlow}
          isLoading={approveRejectMutation.isPending}
          canEnterErrors={canEnterErrors}
          errorsValue={contextAmErrors}
          onErrorsChange={setContextAmErrors}
          issuesValue={contextAmIssues}
          onIssuesChange={setContextAmIssues}
          onClose={() => {
            setContextActionFlow(null);
            setActionNotes("");
          }}
          onConfirm={submitApproveReject}
        />
      )}
    </CustomModal>
  );
}
